import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { gtagTrackBookDemo } from "../util/GoogleTagUtils";

import checkIcon from "../images/zta_checkmark.svg";
import candlestickBg from "../images/bookDemo.svg";
import ninjaTraderLogo from "../images/ninjatrader-vertical-orange.png";

import YouTubePoster from "../components/YouTubePoster";
import { LazyMonthlyReturnsHeatmap } from "../components/MonthlyReturnsHeatmap";
import EquityVsSpyChart from "../components/home/EquityVsSpyChart";
import DiversificationVisual from "../components/home/DiversificationVisual";
import CapitalAllocationVisual from "../components/home/CapitalAllocationVisual";

// ============================================================================
// ANIMATION HELPERS
// ============================================================================

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

function AnimatedSection({ children, className = "", immediate = false }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  const visible = immediate || isInView;
  return (
    <motion.div
      ref={ref}
      initial={immediate ? false : "hidden"}
      animate={visible ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function SectionTitle({ eyebrow, title, highlight, description, align = "center" }) {
  const alignClass = align === "left" ? "text-left mx-0" : "text-center mx-auto";
  return (
    <motion.div variants={fadeUp} className={`max-w-3xl mb-14 md:mb-16 ${alignClass}`}>
      {eyebrow && (
        <span className="inline-block text-brand-light text-sm font-semibold tracking-widest uppercase mb-4">
          {eyebrow}
        </span>
      )}
      <h2 className="font-display text-4xl md:text-5xl lg:text-[3.25rem] text-white tracking-tight leading-[1.1] mb-5">
        {title}
        {highlight ? (
          <>
            {" "}
            <span className="bg-gradient-to-r from-brand-light to-accent-cyan bg-clip-text text-transparent">
              {highlight}
            </span>
          </>
        ) : null}
      </h2>
      {description && (
        <p className={`text-zinc-400 text-lg leading-relaxed ${align === "left" ? "" : "mx-auto"}`}>
          {description}
        </p>
      )}
    </motion.div>
  );
}

function GlassCard({ children, className = "", hover = true }) {
  return (
    <motion.div
      variants={fadeUp}
      className={`bg-white/[0.03] md:backdrop-blur-xl border border-white/[0.07] rounded-2xl ${
        hover ? "hover:bg-white/[0.06] hover:border-white/[0.12] transition-all duration-300" : ""
      } ${className}`}
    >
      {children}
    </motion.div>
  );
}

const PRIMARY_BTN =
  "inline-flex h-12 items-center justify-center whitespace-nowrap px-7 bg-brand hover:bg-brand-light text-white font-semibold text-base rounded-xl transition-all duration-200 shadow-lg shadow-brand/25 hover:shadow-brand/40";
const SECONDARY_BTN =
  "inline-flex h-12 items-center justify-center whitespace-nowrap px-7 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.12] text-white font-semibold text-base rounded-xl transition-all duration-200";

// ============================================================================
// HERO
// ============================================================================

function HeroSection() {
  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden pt-24 pb-16">
      <div
        className="absolute inset-0 opacity-[0.1]"
        style={{
          backgroundImage: `url(${candlestickBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 opacity-[0.45] zta-chart-grid" aria-hidden="true" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[min(720px,90vw)] h-[380px] zta-glow" />
      <div className="absolute top-1/3 left-[18%] w-[min(360px,70vw)] h-[280px] zta-glow-cyan hidden md:block" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full">
        <AnimatedSection immediate>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-end">
            <div className="lg:col-span-7">
              <motion.p
                variants={fadeUp}
                className="text-brand-light text-sm font-semibold tracking-widest uppercase mb-5"
              >
                Automated futures for capitalized investors
              </motion.p>
              <motion.h1
                variants={fadeUp}
                className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[4.75rem] text-white tracking-tight leading-[1.02] mb-6"
              >
                Deploy a portion of your capital with a{" "}
                <span className="bg-gradient-to-r from-brand-light via-brand to-accent-cyan bg-clip-text text-transparent">
                  systematic futures portfolio.
                </span>
              </motion.h1>
            </div>

            <div className="lg:col-span-5">
              <motion.p
                variants={fadeUp}
                className="text-lg md:text-xl text-zinc-400 leading-relaxed mb-8"
              >
                ZTA is diversified automated futures software for investors who want time leverage
                without becoming discretionary day traders. Review the public strategy performance,
                then decide whether a white-glove license fits how you want capital to work.
              </motion.p>
              <motion.div
                variants={fadeUp}
                className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 mb-6"
              >
                <Link to="/book-demo" onClick={gtagTrackBookDemo} className={`${PRIMARY_BTN} w-full sm:w-auto`}>
                  Book a Demo
                </Link>
                <Link to="/strategies" className={`${SECONDARY_BTN} w-full sm:w-auto`}>
                  Explore Strategy Data
                </Link>
              </motion.div>
              <motion.p variants={fadeUp} className="text-zinc-500 text-xs leading-relaxed">
                Recommended starting allocation generally $35,000+. Your capital stays in your
                brokerage account. Historical results are not a guarantee of future performance.
              </motion.p>
            </div>
          </div>

          <motion.div
            variants={fadeUp}
            className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4"
          >
            {[
              { label: "Diversified suite", value: "9+ strategies" },
              { label: "Markets covered", value: "Nasdaq · Gold · Gas" },
              { label: "Your custody", value: "Your brokerage" },
              { label: "Implementation", value: "White-glove setup" },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-2xl border border-white/[0.07] bg-white/[0.03] px-4 py-4"
              >
                <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">
                  {item.label}
                </p>
                <p className="font-display text-xl md:text-2xl text-white tracking-tight">
                  {item.value}
                </p>
              </div>
            ))}
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ============================================================================
// WHY ALLOCATE
// ============================================================================

function WhyAllocateSection() {
  const pillars = [
    {
      title: "Portfolio over one curve",
      body: "Evaluate a diversified suite of automated strategies, not a single black-box system that only works in one market condition.",
    },
    {
      title: "Transparency before commitment",
      body: "Inspect equity history, monthly returns, trade counts, and historical risk publicly before you book a serious license conversation.",
    },
    {
      title: "Time leverage, not screen time",
      body: "The software executes systematically. You allocate capital, review results, and stay in control of the brokerage account.",
    },
  ];

  return (
    <section className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <AnimatedSection>
          <SectionTitle
            eyebrow="Why automated futures"
            title="A more systematic way to put capital to work"
            description="Index funds and discretionary trading both have a place. Automated futures give investors another sleeve: rules-based exposure that does not require watching the tape all day."
          />
          <div className="grid md:grid-cols-3 gap-5">
            {pillars.map((p) => (
              <GlassCard key={p.title} className="p-7" hover={false}>
                <h3 className="font-display text-2xl text-white tracking-tight mb-3">{p.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{p.body}</p>
              </GlassCard>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ============================================================================
// PORTFOLIO PROOF (equity + heatmap)
// ============================================================================

function PortfolioProofSection() {
  return (
    <section id="portfolio-data" className="py-24 md:py-32 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <AnimatedSection>
          <SectionTitle
            eyebrow="Strategy data"
            title="See the portfolio against"
            highlight="traditional markets"
            description="Explore how the Quant Portfolio has performed versus the S&P 500, plus a month-by-month return calendar with compounding and non-compounding views."
          />

          <EquityVsSpyChart className="mb-8" />
          <LazyMonthlyReturnsHeatmap className="mb-10" capitalSource="fixed" />

          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/strategies" className={PRIMARY_BTN}>
              View full strategy data
            </Link>
            <Link to="/book-demo" onClick={gtagTrackBookDemo} className={SECONDARY_BTN}>
              Book a demo
            </Link>
          </motion.div>
          <motion.p
            variants={fadeUp}
            className="text-center text-zinc-600 text-xs max-w-2xl mx-auto mt-6 leading-relaxed"
          >
            Displayed results use the default size per strategy at the portfolio&apos;s minimum
            capital sizing. Past performance does not guarantee future results. Futures trading
            involves substantial risk of loss.
          </motion.p>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ============================================================================
// DIVERSIFICATION
// ============================================================================

function DiversificationSection() {
  return (
    <section className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <AnimatedSection>
          <SectionTitle
            eyebrow="The suite"
            title="Diversified across strategies,"
            highlight="markets, and styles"
            description="Nasdaq breakout and trend systems, gold mean reversion, and natural gas. Independent sleeves designed so one quiet strategy does not define the whole allocation."
          />
          <DiversificationVisual />
        </AnimatedSection>
      </div>
    </section>
  );
}

// ============================================================================
// RISK ARCHITECTURE
// ============================================================================

function RiskArchitectureSection() {
  const items = [
    {
      title: "Historical risk is public",
      body: "Peak-to-trough history sits with the rest of the strategy data, so you can review it before any sales conversation.",
    },
    {
      title: "Position sizing is a decision",
      body: "Capital allocation and contract size are configured with you. Larger size is not automatically better.",
    },
    {
      title: "Session-aware automation",
      body: "Strategies are engineered for liquid U.S. futures markets with defined risk per trade, not gambling tactics.",
    },
    {
      title: "Built for long-horizon capital",
      body: "Red months are part of real markets. The process is designed for investors who can stick with a systematic approach through normal performance variability.",
    },
  ];

  return (
    <section className="py-24 md:py-32 relative">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <AnimatedSection>
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <SectionTitle
                align="left"
                eyebrow="Risk architecture"
                title="Clarity on risk before"
                highlight="you allocate"
                description="Serious capital deserves a clear view of how the portfolio behaves when markets are difficult, not only when the equity curve looks smooth."
              />
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {items.map((item) => (
                <GlassCard key={item.title} className="p-6" hover={false}>
                  <h3 className="font-display text-xl text-white tracking-tight mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{item.body}</p>
                </GlassCard>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ============================================================================
// CAPITAL / CUSTODY
// ============================================================================

function CapitalSection() {
  return (
    <section className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <AnimatedSection>
          <SectionTitle
            eyebrow="Personal capital alignment"
            title="Your account. Your custody."
            highlight="Our software."
            description="ZTA licenses automated strategies into a brokerage account you own and control. We handle implementation and ongoing support. We never take custody of client funds."
          />
          <CapitalAllocationVisual />
        </AnimatedSection>
      </div>
    </section>
  );
}

// ============================================================================
// HOW IT WORKS
// ============================================================================

const VIDEO_CHAPTERS = [
  "The idea",
  "The portfolio",
  "The data",
  "The setup",
  "Who it’s for",
];

function HowItWorksSection() {
  return (
    <section className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <AnimatedSection>
          <SectionTitle
            eyebrow="Overview"
            title="How ZTA"
            highlight="works"
            description="A short walkthrough of the Quant Portfolio: what the strategies do, how automation runs, and what white-glove setup looks like before you commit."
          />
          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-2 mb-8">
            {VIDEO_CHAPTERS.map((chapter, i) => (
              <span
                key={chapter}
                className="px-3.5 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.03] text-xs font-medium text-zinc-300"
              >
                <span className="text-brand-light mr-1.5">{String(i + 1).padStart(2, "0")}</span>
                {chapter}
              </span>
            ))}
          </motion.div>
          <motion.div variants={fadeUp} className="max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] bg-white/[0.02] shadow-2xl shadow-black/30">
              <div className="aspect-video relative">
                <YouTubePoster videoId="Hx4AN1C5Yfg" title="How ZTA Quant Portfolio Works" />
              </div>
            </div>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ============================================================================
// WHITE GLOVE
// ============================================================================

function WhiteGloveSection() {
  const items = [
    {
      title: "Configured for your capital",
      body: "Recommended sizing and risk settings matched to your allocation and tolerance.",
    },
    {
      title: "Live visibility",
      body: "Trade alerts and portfolio reporting so you can review activity without watching the screen all day.",
    },
    {
      title: "Ongoing updates",
      body: "Strategy and infrastructure updates as the suite evolves, handled by the team.",
    },
    {
      title: "Human support",
      body: "A real support relationship through normal performance variability.",
    },
  ];

  return (
    <section className="py-24 md:py-32 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[min(520px,80vw)] h-[280px] zta-glow hidden md:block" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10">
        <AnimatedSection>
          <SectionTitle
            eyebrow="White-glove execution"
            title="We handle the technical path."
            highlight="You stay the investor."
            description="VPS hosting, platform configuration, strategy installation, and ongoing support. You keep custody of the brokerage account."
          />

          <motion.div variants={fadeUp} className="grid sm:grid-cols-2 gap-4 max-w-5xl mx-auto">
            {items.map((item) => (
              <GlassCard key={item.title} className="p-6" hover={false}>
                <div className="flex items-start gap-3">
                  <img src={checkIcon} alt="" className="w-4 h-4 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-display text-xl text-white tracking-tight mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-zinc-400 leading-relaxed">{item.body}</p>
                  </div>
                </div>
              </GlassCard>
            ))}
          </motion.div>

          <motion.div variants={fadeUp} className="mt-10 text-center max-w-2xl mx-auto">
            <p className="text-zinc-500 text-sm leading-relaxed mb-6">
              Automation runs on NinjaTrader. We configure and maintain the stack so you are not left
              assembling it alone.
            </p>
            <img
              src={ninjaTraderLogo}
              alt="NinjaTrader"
              className="mx-auto h-16 md:h-[4.5rem] w-auto"
              loading="lazy"
              decoding="async"
            />
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ============================================================================
// IDEAL CLIENT
// ============================================================================

function IdealClientSection() {
  const fits = [
    "Has meaningful capital for a personal futures brokerage allocation (generally $35,000+ to start)",
    "Thinks in multi-year allocation horizons and can stay systematic through normal market variability",
    "Values diversification, automation, transparency, and time leverage",
    "Wants a white-glove relationship with implementation and ongoing support handled for them",
  ];

  return (
    <section className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <AnimatedSection>
          <SectionTitle
            eyebrow="Who this is for"
            title="Built for capitalized"
            highlight="investors"
            description="ZTA is designed for people allocating a portion of personal capital into a systematic futures sleeve, with custody staying in their own brokerage account."
          />
          <GlassCard className="p-8 max-w-3xl mx-auto" hover={false}>
            <ul className="space-y-4">
              {fits.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-zinc-300 leading-relaxed">
                  <img src={checkIcon} alt="" className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </GlassCard>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ============================================================================
// FAQ (single prop-firm question)
// ============================================================================

const HOME_FAQS = [
  {
    question: "Is there a guarantee?",
    answer:
      "Yes. Yearly and Lifetime licenses include a 12-month participation guarantee: complete the program, and if you do not see a qualifying result, we refund your original license fee. Full terms are covered on the demo.",
  },
  {
    question: "Do I need trading experience to benefit from ZTA?",
    answer:
      "Not at all. Our portfolio is designed to serve both beginners and seasoned traders. The systems are built with robust risk management, so even if you're new, you can deploy them passively and learn as you go.",
  },
  {
    question: "What technical skills do I need to get started?",
    answer:
      "No technical skills are required. We offer a done-for-you setup service where our team handles everything from installation to configuration. We'll get your account up and running so you can start with the systems in place.",
  },
  {
    question: "How much capital do I need to start trading with ZTA?",
    answer:
      "Capital requirements vary depending on which combination of algorithms you trade, the contract types you use (micro vs. E-mini), and your risk management preferences. Rather than a one-size-fits-all number, we recommend booking a demo call with our team so we can assess your situation and recommend the right configuration for your account size and goals. Recommended starting allocation is generally $35,000+.",
  },
  {
    question: "Does this work with prop firms?",
    answer:
      "Yes. The same portfolio can be configured for supported prop firm and funded accounts, with settings that respect common firm rules. If you want to run a personal brokerage account, a funded account, or both, bring that up on the demo and we will walk through the right setup.",
  },
  {
    question: "What platforms do you support?",
    answer:
      "We exclusively support NinjaTrader, which can be run on your personal computer or on a VPS. VPS is our recommended option for optimal performance.",
  },
  {
    question: "What should I expect on the demo call?",
    answer:
      "During the demo call, we discuss your goals, walk you through our algorithmic systems, and determine whether the ZTA Quant Portfolio is a good fit for your capital plan.",
  },
  {
    question: "How much does it cost?",
    answer:
      "License options and payment plans are reviewed on the demo so we can match the right plan to your allocation and goals. Payment plans are available for yearly and lifetime licenses.",
  },
  {
    question: "Are there payment plans available?",
    answer:
      "Yes. Payment plans are available for yearly and lifetime licenses. You can split your payment into 2 or 3 monthly installments with no additional fees.",
  },
];

function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <AnimatedSection>
          <SectionTitle eyebrow="FAQ" title="Have questions?" highlight="We have answers" />
          <motion.div variants={fadeUp} className="space-y-3">
            {HOME_FAQS.map((faq, i) => (
              <div
                key={faq.question}
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setOpenIndex(openIndex === i ? null : i);
                  }
                }}
                role="button"
                tabIndex={0}
                className={`rounded-xl border cursor-pointer transition-all duration-200 ${
                  openIndex === i
                    ? "bg-white/[0.05] border-white/[0.12]"
                    : "bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]"
                }`}
              >
                <div className="flex items-center justify-between px-6 py-5">
                  <span className="text-white font-medium text-sm pr-4">{faq.question}</span>
                  <svg
                    className={`w-5 h-5 text-zinc-500 flex-shrink-0 transition-transform duration-200 ${
                      openIndex === i ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                <AnimatePresence>
                  {openIndex === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-5">
                        <p className="text-zinc-400 text-sm leading-relaxed">{faq.answer}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ============================================================================
// FINAL CTA
// ============================================================================

function FinalCtaSection() {
  return (
    <section className="py-24 md:py-32">
      <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
        <AnimatedSection>
          <motion.p
            variants={fadeUp}
            className="text-brand-light text-sm font-semibold tracking-widest uppercase mb-4"
          >
            Next step
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-display text-4xl md:text-5xl text-white tracking-tight leading-tight mb-5"
          >
            Review the data. Then talk with the team.
          </motion.h2>
          <motion.p variants={fadeUp} className="text-zinc-400 text-lg mb-10 leading-relaxed">
            Explore the public strategy portfolio first. If it looks like a serious fit for your
            capital plan, book a demo and we&apos;ll walk through allocation, automation, and
            implementation.
          </motion.p>
          <motion.div
            variants={fadeUp}
            className="flex flex-col sm:flex-row sm:items-center justify-center gap-3"
          >
            <Link to="/book-demo" onClick={gtagTrackBookDemo} className={PRIMARY_BTN}>
              Book a Demo
            </Link>
            <Link to="/strategies" className={SECONDARY_BTN}>
              Explore Strategy Data
            </Link>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ============================================================================
// PAGE
// ============================================================================

const Home = () => {
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return undefined;
    const timer = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className="bg-[#0c0e14] min-h-screen overflow-hidden"
      style={{
        backgroundImage:
          "linear-gradient(rgba(129,140,248,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(129,140,248,0.035) 1px, transparent 1px)",
        backgroundSize: "64px 64px",
      }}
    >
      <HeroSection />
      <WhyAllocateSection />
      <PortfolioProofSection />
      <DiversificationSection />
      <RiskArchitectureSection />
      <CapitalSection />
      <HowItWorksSection />
      <WhiteGloveSection />
      <IdealClientSection />
      <FAQSection />
      <FinalCtaSection />
    </div>
  );
};

export default Home;
